import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Select,
  Button,
  SimpleGrid,
  Tag,
  TagLabel,
  Tooltip,
  IconButton,
  Text,
  VStack,
  HStack,
  useToast,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  Divider
} from '@chakra-ui/react';
import { InfoIcon, CheckIcon, CloseIcon, EditIcon } from '@chakra-ui/icons';

const LEAD_FIELDS = {
  phone: { label: 'Phone Number', required: true },
  brideAndGroomName: { label: 'Bride and Groom Name', required: false },
  email: { label: 'Email', required: false },
  pointOfContact: { label: 'Point of Contact', required: false },
  stage: { label: 'Stage', required: false },
  priority: { label: 'Priority', required: false },
  leadSource: { label: 'Lead Source', required: false, useConstants: true },
  events: { label: 'Event Types', required: false },
  guestCapacity: { label: 'Guest Capacity', required: false },
  venues: { label: 'Venues', required: false },
  cities: { label: 'Cities', required: false },
  budgetAmount: { label: 'Budget Amount', required: false },
  foodPreference: { label: 'Food Preference', required: false },
  alcoholPreference: { label: 'Alcohol Preference', required: false },
  roomCount: { label: 'Room Count', required: false },
  residenceGuestCount: { label: 'Residence Guest Count', required: false },
  customerNotes: { label: 'Customer Notes', required: false },
  nextFollowUpTime: { label: 'Next Follow Up Time', required: false },
};

const HeaderMapping = ({ sheet, onUpdateMapping, loading, constants }) => {
  console.log('HeaderMapping mounted with constants:', constants);
  
  useEffect(() => {
    console.log('HeaderMapping constants changed:', constants);
  }, [constants]);
  const [mapping, setMapping] = useState({
    phone: '',
    brideAndGroomName: '',
    customerNotes: '',
    events: '',
    venues: []
  });

  useEffect(() => {
    if (sheet?.headerMapping) {
      // Merge existing mapping with default values
      setMapping(prev => ({
        ...prev,
        ...sheet.headerMapping
      }));
    } else {
      // Reset to default mapping if no mapping exists
      setMapping({
        phone: '',
        brideAndGroomName: '',
        customerNotes: '',
        events: '',
        venues: []
      });
    }
  }, [sheet]);

  const handleChange = (leadField, headerIndex) => {
    const newMapping = { ...mapping };
    
    // Remove old mapping if exists
    Object.keys(newMapping).forEach(key => {
      if (newMapping[key] === headerIndex) {
        delete newMapping[key];
      }
    });

    // Add new mapping
    if (headerIndex !== '') {
      newMapping[leadField] = headerIndex;
    }

    setMapping(newMapping);
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      const missingRequired = Object.entries(LEAD_FIELDS)
        .filter(([_, config]) => config.required)
        .find(([field]) => !mapping[field]);

      if (missingRequired) {
        throw new Error(`${LEAD_FIELDS[missingRequired[0]].label} mapping is required`);
      }

      // Format phone number mapping to remove spaces
      const formattedMapping = {
        ...mapping,
        phone: mapping.phone ? mapping.phone.toString().trim() : mapping.phone
      };

      await onUpdateMapping(sheet.sheetId, sheet.gid, formattedMapping);
      toast({
        title: 'Success',
        description: 'Header mapping updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update header mapping',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
      throw error;
    }
  };

  const isHeaderMapped = (headerIndex) => {
    return Object.values(mapping).includes(headerIndex);
  };

  const toast = useToast();

  const handleSaveWithToast = async () => {
    try {
      await handleSave();
      toast({
        title: 'Mapping saved',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: 'Error saving mapping',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
      throw error; // Re-throw to prevent further execution
    }
  };

  return (
    <Card>
      <CardHeader>
        <HStack justify="space-between">
          <Heading size="md">Header Mapping</Heading>
          <Tag colorScheme="blue">{Object.keys(mapping).filter(k => mapping[k]).length} Fields Mapped</Tag>
        </HStack>
      </CardHeader>

      <CardBody>
        {!(sheet?.headerList || []).length ? (
          <Text color="gray.500">
            No headers available. Please refresh the sheet data.
          </Text>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {Object.entries(LEAD_FIELDS).map(([field, config]) => {
              const headerIndex = mapping[field];
              const headerName = sheet?.headerList?.[headerIndex];
              const isMapped = headerIndex !== undefined;
              const isRequired = config.required;
              
              return (
                <Card 
                  key={field} 
                  variant="outline"
                  bg={isRequired ? (isMapped ? 'green.50' : 'red.50') : (isMapped ? 'green.50' : 'white')}
                >
                  <CardHeader p={3} pb={2}>
                    <HStack justify="space-between">
                      <Text fontWeight="medium">{config.label}</Text>
                      {isRequired && (
                        <Badge colorScheme="red" variant="subtle">Required</Badge>
                      )}
                    </HStack>
                  </CardHeader>

                  <CardBody p={3} pt={0}>
                    <FormControl>
                      {field === 'leadSource' ? (
                        <Select
                          size="sm"
                          value={mapping[field]?.toString() || ''}
                          onChange={(e) => handleChange(field, e.target.value?.toString() || '')}
                          isDisabled={loading}
                          bg="white"
                          placeholder="Select source"
                        >
                          {console.log('HeaderMapping constants:', constants)}
                          {console.log('Lead sources:', constants?.LEAD_SOURCES)}
                          {constants?.LEAD_SOURCES?.values?.map((source) => (
                            <option
                              key={source._id}
                              value={source._id}
                            >
                              {source.name}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Select
                          size="sm"
                          value={mapping[field] || ''}
                          onChange={(e) => handleChange(field, e.target.value)}
                          isDisabled={loading}
                          bg="white"
                          placeholder="Select header"
                        >
                          {(sheet?.headerList || []).map((header, index) => (
                            <option
                              key={index}
                              value={index}
                              disabled={isHeaderMapped(index) && mapping[field] !== index}
                            >
                              {header}
                            </option>
                          ))}
                        </Select>
                      )}
                    </FormControl>

                    {headerName && (
                      <HStack mt={2} spacing={2} fontSize="sm" color="gray.600">
                        <CheckIcon color="green.500" />
                        <Text>Mapped to: {headerName}</Text>
                        <IconButton
                          size="xs"
                          icon={<CloseIcon />}
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleChange(field, '')}
                          isDisabled={loading}
                          aria-label="Remove mapping"
                          ml="auto"
                        />
                      </HStack>
                    )}
                  </CardBody>
                </Card>
              );
            })}
          </SimpleGrid>
        )}

        <Button
          mt={6}
          colorScheme="blue"
          onClick={handleSaveWithToast}
          isLoading={loading}
          isDisabled={!(sheet?.headerList || []).length}
          leftIcon={<CheckIcon />}
          width="full"
        >
          Save Mapping
        </Button>
      </CardBody>
    </Card>
  );
};

export default HeaderMapping;
